import { defineStore } from '@/store/define-store';
import usePeopleStore from '@/modules/people/store';
import {
  REGULAR_SCHEDULE_STATUS_CATEGORY,
  REGULAR_SCHEDULES_BEHAVIOURS,
  REGULAR_SCHEDULES_STATUS,
} from '@/modules/people/definitions';
import { getDateDifferenceFromNowInDays } from '@/modules/people/composables/agreed-days-times';
import {
  agreedHoursOptions,
  getDefaultAgreedHour,
  getDefaultWorkPeriod,
} from '@/modules/people/composables/working-hours';
import agreedHoursSvc from '@/lib/request/svc/compliance/agreed-hours';
import workPeriodsSvc from '@/lib/request/svc/compliance/work-period';
import useWebappStore from '@/store';

// TODO: upcoming code from frontend/vnext/src/modules/people/components/people-form-tab-employment/edit-work-hours-agreed-hours-overtime-days-times-edit-days-times.vue
import {
  bucketRegularSchedules,
  calculateAgreedDayTotalHours,
  calculateTotalHoursPerWorkPeriod,
  getEmployeeWorkplaces,
  pickRegularSchedule,
  weekNumberMapper,
  weekTextToNumberMapper,
} from '@/modules/people/composables/agreed-days-times';
import areasListByLocations from '@/lib/request/webapp/location/area/list-by-locations';
import useRootStore from '@/modules/people/store/root';
import { getWorkplaces } from '@/modules/people/facades/employee-workplace';
import { WORK_PERIOD } from '@/modules/people/definitions';
import { check as checkFF } from '@/lib/feature-flag';
import { DateTime } from 'luxon';
import { agreedHoursSearch } from '@/lib/request/webapp/people/agreed-hours-get';
import { getTranslations } from '@/lib/i18next';
import { agreedHoursGetGeneralConfiguration } from '@/lib/request/webapp/people/agreed-hours-configuration';

function getWorkPeriodModel() {
  return {
    installationId: '',
    employeeId: '',
    cycle: '',
    dayOfWeek: '',
    startDate: '',
    startCycleDate: '',
    endCycleDate: '',
    cycleDurationDays: 0,
    days: 0,
  };
}

function getAgreedHourModel() {
  return {
    installationId: '',
    employeeId: '',
    threshold: 0,
    applyThreshold: false,
    workPeriod: getWorkPeriodModel(),
    details: null,
    workingDaysPerCycle: 0,
  };
}

// Everything the days/times total is derived from. The cycle belongs here as
// well as the rows: a one-week schedule is multiplied by the cycle length, so
// changing it changes the total without touching a single row.
function serialiseThresholdInputs(agreedHour) {
  return JSON.stringify({
    weeks: agreedHour?.details?.weeks ?? null,
    cycle: agreedHour?.workPeriod?.cycle ?? null,
  });
}

const useWorkingHoursStore = defineStore('webapp:people:working-hours', {
  state: () => {
    return {
      allAgreedHoursData: [],
      agreedHour: getAgreedHourModel(),
      listWorkPeriod: [],
      validationError: {},
      dataMap: {},
      selectedOption: null,
      agreedDaysTimesThreshold: 0,
      agreedDaysTimesApplyThreshold: false,
      originalAgreedHour: null,
      workplaceMap: {},
      workplaceMapFailed: false,
      workplaceLookupFailed: false,
      loadedThresholdInputs: null,
    };
  },
  actions: {
    setAgreedHour(agreedHour = getAgreedHourModel()) {
      this.agreedHour = agreedHour;

      // TODO: split work period out of agreed hours
      this.setAgreedHourWorkPeriod(agreedHour.workPeriod);
      this.refreshWorkplaceCoverage();
      this.calculateAgreedDaysTimesThreshold();
      this.calculateWorkingDaysPerCycle();

      if (!this.originalAgreedHour) {
        this.originalAgreedHour = {
          threshold: agreedHour.threshold,
          workingDaysPerCycle: this.agreedHour.workingDaysPerCycle,
          cycle: agreedHour.workPeriod?.cycle,
          agreedDaysTimesThreshold: this.agreedDaysTimesThreshold,
        };
      }
      this.validateAreas();
    },
    getAgreedHour() {
      return this.agreedHour || getAgreedHourModel();
    },
    // Area WorkType lookup for the threshold calculation.
    // evaluateCoverage is false while an agreed hour is still being loaded: the
    // map is already the new employee's but the rows are still the previous
    // one's, and measuring one against the other flags every location they do
    // not share. fetchAgreedHour evaluates it once the rows arrive.
    setWorkplaceMap(workplaceMap = {}, evaluateCoverage = true) {
      this.workplaceMap = workplaceMap;
      if (evaluateCoverage) {
        // Being handed a map is itself a successful load, so an earlier failed
        // lookup no longer stands: the editors re-seed the store this way.
        this.workplaceLookupFailed = false;
        this.refreshWorkplaceCoverage();
      }
      this.calculateAgreedDaysTimesThreshold();
      this.validateAreas();
    },
    // The lookup failing and the lookup coming back short are different things,
    // but both mean a row cannot be classified, so they drive the same flag.
    refreshWorkplaceCoverage() {
      this.workplaceMapFailed =
        this.workplaceLookupFailed || this.hasUnreadableRowLocation();
    },
    async loadWorkplaceMap() {
      this.workplaceMap = {};
      this.workplaceMapFailed = false;
      this.workplaceLookupFailed = false;
      const peopleState = usePeopleStore();
      const workplaceIds = (peopleState.getEmployeeLocations() || []).map(
        workplace => workplace.Company
      );
      if (!workplaceIds.length) {
        return;
      }
      const { data: areas } = await areasListByLocations(workplaceIds);
      this.setWorkplaceMap(
        getEmployeeWorkplaces(
          workplaceIds,
          areas || [],
          useRootStore().getLocations(),
          getWorkplaces()
        ),
        false
      );
    },
    setAgreedHourWorkPeriodCycle(cycle = '') {
      this.agreedHour.workPeriod.cycle = cycle;
      this.calculateAgreedDaysTimesThreshold();
      this.calculateWorkingDaysPerCycle();
      this.validateAreas();
    },
    getAgreedHourWorkPeriod() {
      return this.agreedHour.workPeriod || {};
    },
    isSetAgreedHourWorkPeriod() {
      return !!this.agreedHour.workPeriod;
    },
    getAgreedHourWorkPeriodCycle() {
      return this.agreedHour.workPeriod.cycle || 'WEEKLY';
    },
    isSetAgreedHourWorkPeriodCycle() {
      return !!this.agreedHour.workPeriod.cycle;
    },
    /**
     * @param {Object|null} details
     */
    setAgreedHourDetails(details = null) {
      this.agreedHour.details = details;
    },
    getAgreedHourThreshold() {
      return this.agreedHour.threshold || 0;
    },
    isSetAgreedHourThreshold() {
      return Object.keys(this.getAgreedHourThreshold()).length > 0;
    },
    setAgreedHourThreshold(threshold = 0) {
      this.agreedHour.threshold = threshold;
    },
    getAgreedHourDetails() {
      return this.agreedHour.details || null; // TODO: this should return an empty array and we need another store function for isAgreedHourDetailsSet
    },
    getDataMapAgreedHourDetails() {
      return this.dataMap.AgreedHour.details || null;
    },
    /**
     * @param {Object[]} weeks
     *  @param {String} weeks[].week
     */
    setAgreedHourDetailsWeeks(weeks = []) {
      if (!this.getAgreedHourDetails()) {
        this.setAgreedHourDetails({});
      }

      this.agreedHour.details.weeks = weeks;
      this.calculateAgreedDaysTimesThreshold();
      this.calculateWorkingDaysPerCycle();
      this.validateAreas();
    },
    setAgreedHourWorkPeriod(workPeriod = getWorkPeriodModel()) {
      this.agreedHour.workPeriod = workPeriod;
    },
    /**
     * @param {Object} dataMap
     *   @param {Number} dataMap.StressProfile
     *   @param {Object} dataMap.AgreedHour
     */
    setDataMap(dataMap) {
      this.dataMap = dataMap;
    },
    /**
     * @param {Number} stressProfile
     */
    setStressProfile(stressProfile = 1) {
      this.dataMap.StressProfile = stressProfile;
    },
    getStressProfile() {
      return this.dataMap.StressProfile || 1;
    },
    setListWorkPeriod(listWorkPeriod = []) {
      this.listWorkPeriod = listWorkPeriod;
    },
    addListWorkPeriod(listWorkPeriod = {}) {
      this.listWorkPeriod.push(listWorkPeriod);
    },
    setAgreedHoursStartDate(startDate = '') {
      this.agreedHoursStartDate = startDate;
    },
    async fetchAgreedHour() {
      this.originalAgreedHour = null;
      this.loadedThresholdInputs = null;

      const peopleState = usePeopleStore();
      const employeeId = peopleState.getEmployee().Id;

      const webappState = useWebappStore();
      await webappState.fetchSetup();

      try {
        await this.loadWorkplaceMap();
      } catch (e) {
        // Without area types the total cannot be derived safely; keep the
        // stored threshold instead of recomputing with every row counted.
        this.workplaceLookupFailed = true;
        this.workplaceMapFailed = true;
        console.warn('not possible to load the employee areas', e);
      }

      let agreedHours;
      try {
        if (this.shouldUseFwwAgreedHoursUI) {
          const currentDate = DateTime.fromJSDate(new Date()).toUTC();
          const startDate = currentDate.minus({ years: 3 }).toISO();
          const endDate = currentDate.plus({ years: 1 }).toISO();
          const { success, data } = await agreedHoursSearch(
            employeeId,
            false,
            startDate,
            endDate,
            false,
            true,
            false,
            currentDate.toISO()
          );

          // Same selection the multi-week editor applies, so the schedule the
          // store loads and the one the editor installs cannot disagree.
          if (success && data?.agreedHour) {
            this.allAgreedHoursData = data;

            agreedHours =
              pickRegularSchedule(
                bucketRegularSchedules(data.agreedHour, data?.currentVersion)
              ) || undefined;

            this.setAgreedHoursStartDate(agreedHours?.workPeriod?.startDate);
          }

          if (!agreedHours) {
            const response = await agreedHoursSvc.get(
              webappState.getInstanceId(),
              employeeId
            );
            agreedHours = response?.agreedHour;
          }
        } else {
          const response = await agreedHoursSvc.get(
            webappState.getInstanceId(),
            employeeId
          );

          agreedHours = response?.agreedHour;
        }
      } catch (e) {
        console.warn(
          'not possible to fetch the agreed hours from the service using default config',
          e
        );
      } finally {
        if (!agreedHours) {
          agreedHours = getDefaultAgreedHour(
            webappState.getInstanceId(),
            employeeId
          );
        }
      }

      this.setAgreedHour(agreedHours);
      // Taken off the store, not the payload: setAgreedHour substitutes its own
      // work-period model when the payload omits one, and a snapshot of the
      // payload would differ from it immediately.
      this.loadedThresholdInputs = serialiseThresholdInputs(this.agreedHour);
      this.validateAreas();
    },
    async fetchWorkPeriods() {
      const webappState = useWebappStore();
      await webappState.fetchSetup();
      let collection = [getDefaultWorkPeriod()];

      try {
        const response = await workPeriodsSvc.get(webappState.getInstanceId());

        collection = response?.workPeriodCollection
          ? [...collection, ...response.workPeriodCollection]
          : [...collection];
      } catch (e) {
        console.warn(
          'not possible to fetch the work period from the service using default config',
          e
        );
      }

      this.setListWorkPeriod(collection);
    },
    /**
     * @param {String|Number} id
     * @param {Any} value
     */
    setValidationError(id, value) {
      this.validationError[id] = value;
    },
    /**
     * @param {String|Number} id
     * @return {Any}
     */
    getValidationError(id) {
      return this.validationError[id];
    },
    /**
     * @param {Object} errors
     */
    setValidationErrors(errors = {}) {
      this.validationError = errors;
    },
    /**
     * @return {Object}
     */
    getValidationErrors() {
      return this.validationError;
    },
    /**
     * @param {String} selectedOption
     */
    setSelectedOption(selectedOption = '') {
      this.selectedOption = selectedOption;
      this.validateWorkPeriod();
    },
    getSelectedOption() {
      return this.selectedOption || agreedHoursOptions.TOTAL_AGREED_HOURS;
    },
    /**
     * @param {Number} threshold
     */
    setAgreedDaysTimesThreshold(threshold = 0) {
      this.agreedDaysTimesThreshold = threshold;
    },
    /**
     * @return {Number}
     */
    getAgreedDaysTimesThreshold() {
      return this.agreedDaysTimesThreshold || 0;
    },
    // TODO: upcoming code from frontend/vnext/src/modules/people/components/people-form-tab-employment/edit-work-hours-agreed-hours-overtime-days-times-edit-days-times.vue
    calculateAgreedDaysTimesThreshold() {
      if (this.workplaceMapFailed && this.getAgreedHourDetails()?.weeks) {
        this.setAgreedDaysTimesThreshold(Number(this.agreedHour.threshold) || 0);
        return;
      }

      if (checkFF('cmpl1-1566-agreed-hours-multi-week')) {
        // TODO: REFACTORING of this bit is required, quite hard to unpack
        this.setAgreedDaysTimesThreshold(
          // the result of this reduce will get multiplied by
          this.getAgreedHourDetails()?.weeks?.reduce((reduced, week) => {
            if (
              weekTextToNumberMapper[week.week] <=
                WORK_PERIOD[this.getAgreedHourWorkPeriodCycle()].weeks &&
              week?.days
            ) {
              reduced += week.days?.reduce((total, day) => {
                const dayTotalHours = calculateAgreedDayTotalHours(
                  day,
                  this.workplaceMap
                );
                return (total += Number(dayTotalHours));
              }, 0);
            }
            return reduced;
          }, 0) *
            // the result of the above reduce will get multiplied by
            (this.getAgreedHourDetails()?.weeks?.length === 1 &&
            this.getAgreedHourDetails()?.weeks[0].week === 'ONE'
              ? WORK_PERIOD[this.getAgreedHourWorkPeriodCycle()].weeks
              : 1) || 0
        );

        return;
      }

      const getFirstWeekData = this.getAgreedHourDetails()?.weeks?.find(
        w => w.week === weekNumberMapper.firstWeek
      );

      const details = getFirstWeekData?.days;

      this.setAgreedDaysTimesThreshold(
        calculateTotalHoursPerWorkPeriod(
          details?.reduce((total, day) => {
            const dayTotalHours = calculateAgreedDayTotalHours(
              day,
              this.workplaceMap
            );
            return (total += Number(dayTotalHours));
          }, 0)
        )
      );
    },
    /**
     * @param {Boolean} apply
     */
    setAgreedDaysTimesApplyThreshold(apply = false) {
      this.agreedDaysTimesApplyThreshold = apply;
    },
    getAgreedHourApplyThreshold() {
      return this.agreedHour.applyThreshold;
    },
    setAgreedHourApplyThreshold(applyThreshold = false) {
      this.agreedHour.applyThreshold = applyThreshold;
    },
    getAgreedHourWorkingDaysPerCycle() {
      return this.agreedHour.workingDaysPerCycle ?? 0;
    },
    setAgreedHourWorkingDaysPerCycle(workingDaysPerCycle = 0) {
      this.agreedHour.workingDaysPerCycle = workingDaysPerCycle;
    },
    calculateWorkingDaysPerCycle() {
      const details = this.getAgreedHourDetails();
      if (!details?.weeks?.length) {
        if (details?.weeks) {
          this.setAgreedHourWorkingDaysPerCycle(0);
        }
        return;
      }
      const count = details.weeks.reduce(
        (total, week) =>
          total +
          (week.days?.filter(d => d.isChecked || d.shifts?.length > 0).length ||
            0),
        0
      );
      this.setAgreedHourWorkingDaysPerCycle(count);
    },
    setAgreedHourStatus(status = '') {
      this.agreedHour.status = status;
    },
    validateWorkPeriod(option = this.getSelectedOption()) {
      const t = getTranslations({
        module: 'people',
        file: 'people-form-tab-employment-edit:working-hours',
      });

      const workPeriod = this.getAgreedHourWorkPeriod();
      const threshold = this.getAgreedHourThreshold();

      const isValid = threshold
        ? workPeriod.cycle && workPeriod.dayOfWeek && threshold
        : true;

      this.setValidationError(
        'workperiod',
        !isValid && option === agreedHoursOptions.TOTAL_AGREED_HOURS
          ? t('error-work-period-select')
          : undefined
      );
      this.validateAreas(option);
    },
    // Rows changed since the last load; the stored threshold no longer
    // describes them, so it would have to be recomputed on save.
    hasEditedLoadedDetails() {
      return (
        this.loadedThresholdInputs !== null &&
        serialiseThresholdInputs(this.agreedHour) !== this.loadedThresholdInputs
      );
    },
    // A location that came back with no areas at all means the caller could not
    // read it, not that it has none: the areas list is scoped to the caller's
    // own locations, so an employee location the caller is not assigned to
    // returns empty with a 200. Rows there cannot be classified as worked or
    // not, so the stored threshold has to stand. A single unresolvable area in
    // a location that did load is a deleted area, not a gap in what we can see.
    hasUnreadableRowLocation() {
      const weeks = this.agreedHour?.details?.weeks;
      if (!weeks?.length) {
        return false;
      }
      return weeks.some(week =>
        (week?.days || []).some(day =>
          (day?.shifts || []).some(
            shift => !this.workplaceMap?.[shift?.location]?.areas?.length
          )
        )
      );
    },
    // Without area types an edited days/times total cannot be derived; block
    // that option rather than save a threshold that no longer matches the
    // rows. Untouched rows keep their stored threshold and stay saveable, so
    // an area-load failure never freezes unrelated employment edits.
    // Kept separate from validateWorkPeriod: this one is safe to run on load,
    // the work-period check only applies once the legacy option is chosen.
    validateAreas(option = this.getSelectedOption()) {
      const t = getTranslations({
        module: 'people',
        file: 'people-form-tab-employment-edit:working-hours',
      });
      this.setValidationError(
        'areas',
        this.workplaceMapFailed &&
          option === agreedHoursOptions.SPECIFIC_DAYS_TIMES &&
          this.hasEditedLoadedDetails()
          ? t('areas-load-error')
          : undefined
      );
    },
    fetchStatusCategory() {
      let statusCategory;

      if (this.shouldUseFwwAgreedHoursUI) {
        const agreedHour = this.getAgreedHour();
        const status = agreedHour.status;
        const difference = getDateDifferenceFromNowInDays(
          new Date(this.agreedHoursStartDate)
        );

        if (
          [
            REGULAR_SCHEDULES_STATUS.ACCEPTED,
            REGULAR_SCHEDULES_STATUS.FORCED,
          ].includes(status)
        ) {
          statusCategory =
            difference <= 0
              ? REGULAR_SCHEDULE_STATUS_CATEGORY.CURRENT
              : REGULAR_SCHEDULE_STATUS_CATEGORY.UPCOMING;
        } else if (status === REGULAR_SCHEDULES_STATUS.PENDING) {
          statusCategory = REGULAR_SCHEDULE_STATUS_CATEGORY.PENDING;
        }
      }

      this.setStatusCategory(statusCategory);
    },
    setStatusCategory(statusCategory = '') {
      this.statusCategory = statusCategory;
    },
    getStatusCategory() {
      return this.statusCategory;
    },
    async fetchAgreedHoursGeneralConfiguration(employeeId) {
      if (!checkFF('ints-1484-regular-schedule-ui') || !employeeId) {
        return false;
      }

      const { success, data } = await agreedHoursGetGeneralConfiguration(
        employeeId
      );

      this.behaviour = data?.configuration?.behaviour;
      this.setShouldUseFwwAgreedHoursUI(
        success &&
          data?.configuration?.behaviour !==
            REGULAR_SCHEDULES_BEHAVIOURS.AGREED_HOURS
      );
      this.setNeedsEmployeeApproval(data?.configuration?.needsEmployeeApproval);
    },
    setShouldUseFwwAgreedHoursUI(value = false) {
      this.shouldUseFwwAgreedHoursUI = value;
    },
    getShouldUseFwwAgreedHoursUI() {
      return this.shouldUseFwwAgreedHoursUI;
    },
    setNeedsEmployeeApproval(needsEmployeeApproval = '') {
      this.needsEmployeeApproval = needsEmployeeApproval;
    },
    getNeedsEmployeeApproval() {
      return this.needsEmployeeApproval;
    },
    hasAgreedHourChangedForLeave() {
      if (!this.originalAgreedHour) {
        return false;
      }
      return (
        this.agreedHour.threshold !== this.originalAgreedHour.threshold ||
        this.agreedHour.workingDaysPerCycle !== this.originalAgreedHour.workingDaysPerCycle ||
        this.agreedHour.workPeriod?.cycle !== this.originalAgreedHour.cycle ||
        (this.agreedDaysTimesThreshold || 0) !== (this.originalAgreedHour.agreedDaysTimesThreshold || 0)
      );
    },
  },
});

export default useWorkingHoursStore;
